import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useContacts } from '../../contacts/api/useContacts';
import { useCurrencies } from '../../currencies/api/useCurrencies';
import { ErrorMessage } from '../../../shared/ui/ErrorMessage';
import { Loading } from '../../../shared/ui/Loading';
import { EmptyState } from '../../../shared/ui/EmptyState';
import { FilterSheet } from '../../../shared/ui/FilterSheet';
import { AgeBucketFilter } from '../components/AgeBucketFilter';
import { useAllOpenDebts, type AgeBucket } from '../api/useDebts';
import { groupDebts, totalsByCurrency } from '../lib/groupDebts';
import Decimal from 'decimal.js';

type Side = 'receivables' | 'payables';
interface Filters {
  contactId: string;
  currencyId: string;
  ageBucket: AgeBucket | '';
}
const EMPTY: Filters = { contactId: '', currencyId: '', ageBucket: '' };

function amount(value: Decimal): string {
  const digits = Math.max(2, value.decimalPlaces());
  const [whole, fraction] = value.toFixed(digits).split('.');
  return `${whole?.replace(/\B(?=(\d{3})+(?!\d))/g, '\u202f')}.${fraction}`;
}

export function DebtGroupsPage({ side }: { side: Side }) {
  const { t, i18n } = useTranslation();
  const [filters, setFilters] = useState<Filters>(EMPTY);
  const [filterOpen, setFilterOpen] = useState(false);
  const contacts = useContacts({ includeArchived: true });
  const currencies = useCurrencies(true);
  const debts = useAllOpenDebts(side, {
    ...(filters.contactId ? { contactId: filters.contactId } : {}),
    ...(filters.currencyId ? { currencyId: filters.currencyId } : {}),
    ...(filters.ageBucket ? { ageBucket: filters.ageBucket } : {}),
  });

  function setField<K extends keyof Filters>(key: K, value: Filters[K]) {
    setFilters((current) => ({ ...current, [key]: value }));
  }

  const rows = debts.data ?? [];
  const groups = groupDebts(rows);
  const totals = [...totalsByCurrency(rows)].sort(([a], [b]) =>
    (currencies.data?.find((c) => c.id === a)?.code ?? a).localeCompare(
      currencies.data?.find((c) => c.id === b)?.code ?? b,
    ),
  );
  const contactName = (id: string) =>
    contacts.data?.find((contact) => contact.id === id)?.name ?? id.slice(0, 8);
  const currencyCode = (id: string) =>
    currencies.data?.find((currency) => currency.id === id)?.code ?? id.slice(0, 3);
  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const isReceivable = side === 'receivables';
  const dateFormat = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium' });

  return (
    <>
      <AgeBucketFilter value={filters.ageBucket} onChange={(next) => setField('ageBucket', next)} />
      <div className="search-row">
        <button
          type="button"
          className={`filter-pill${activeFilterCount ? ' has-filters' : ''}`}
          aria-expanded={filterOpen}
          onClick={() => setFilterOpen(true)}
          style={{ flex: 1, justifyContent: 'center' }}
        >
          {t('common.filters')}
          {activeFilterCount ? (
            <span className="filter-pill__count">{activeFilterCount}</span>
          ) : null}
        </button>
      </div>
      {filterOpen ? (
        <FilterSheet onClose={() => setFilterOpen(false)}>
          <div className="field">
            <label htmlFor="debt-contact-filter">{t('common.filter.all_contacts')}</label>
            <select
              id="debt-contact-filter"
              value={filters.contactId}
              onChange={(e) => setField('contactId', e.target.value)}
            >
              <option value="">{t('common.filter.all_contacts')}</option>
              {contacts.data?.map((contact) => (
                <option key={contact.id} value={contact.id}>
                  {contact.name}
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="debt-currency-filter">{t('common.filter.all_currencies')}</label>
            <select
              id="debt-currency-filter"
              value={filters.currencyId}
              onChange={(e) => setField('currencyId', e.target.value)}
            >
              <option value="">{t('common.filter.all_currencies')}</option>
              {currencies.data?.map((currency) => (
                <option key={currency.id} value={currency.id}>
                  {currency.code} — {currency.name}
                </option>
              ))}
            </select>
          </div>
          {activeFilterCount ? (
            <button type="button" className="btn btn--ghost" onClick={() => setFilters(EMPTY)}>
              {t('common.reset_filters')}
            </button>
          ) : null}
        </FilterSheet>
      ) : null}

      {debts.isLoading || contacts.isLoading || currencies.isLoading ? <Loading /> : null}
      {debts.error ? <ErrorMessage error={debts.error} /> : null}
      {contacts.error ? <ErrorMessage error={contacts.error} /> : null}
      {currencies.error ? <ErrorMessage error={currencies.error} /> : null}
      {debts.data && contacts.data && currencies.data ? (
        rows.length === 0 ? (
          <EmptyState
            icon={isReceivable ? '📥' : '📤'}
            title={t(isReceivable ? 'debts.receivables_empty' : 'debts.payables_empty')}
          />
        ) : (
          <>
            <section className="debt-overview" aria-label={t('debts.filtered_totals')}>
              <h2>{t('debts.filtered_totals')}</h2>
              <ul>
                {totals.map(([id, total]) => (
                  <li key={id}>
                    <strong dir="ltr">
                      {amount(total)} {currencyCode(id)}
                    </strong>
                  </li>
                ))}
              </ul>
            </section>
            <ul
              className="debt-group-list"
              aria-label={t(isReceivable ? 'debts.tab_receivables' : 'debts.tab_payables')}
            >
              {groups.map((group) => (
                <li key={`${group.contactId}:${group.currencyId}`}>
                  <details className="debt-group">
                    <summary>
                      <span className="debt-group__identity">
                        <strong>{contactName(group.contactId)}</strong>
                        <span>
                          {currencyCode(group.currencyId)} ·{' '}
                          {group.rows.length === 1
                            ? t('debts.single_open_debt')
                            : t('debts.multiple_open_debts', { n: group.rows.length })}
                        </span>
                      </span>
                      <span className="debt-group__amount" dir="ltr">
                        {amount(group.total)} {currencyCode(group.currencyId)}
                      </span>
                    </summary>
                    <div className="debt-group__details">
                      {group.rows.map((row) => (
                        <div key={row.id} className="debt-group__operation">
                          <div>
                            <strong dir="ltr">
                              {amount(new Decimal(row.outstandingAmount))}{' '}
                              {currencyCode(row.currencyId)}
                            </strong>
                            <span>
                              {dateFormat.format(new Date(row.createdAt))} ·{' '}
                              {t(`debts.payment_status.${row.paymentStatus}`)}
                            </span>
                          </div>
                          <Link
                            className="btn btn--secondary btn--sm"
                            to={`/debts/${side}/${row.id}/${isReceivable ? 'receive' : 'pay'}`}
                          >
                            {t(isReceivable ? 'debts.receive_action' : 'debts.pay_action')}
                          </Link>
                        </div>
                      ))}
                    </div>
                  </details>
                </li>
              ))}
            </ul>
          </>
        )
      ) : null}
    </>
  );
}
